import React, { useEffect, useMemo, useRef, useState } from "react";

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFKC")
    .toLocaleLowerCase()
    // Keep contractions as one token: he'd -> hed, Widget's -> widgets.
    // Replacing apostrophes with spaces shifts display-word indexes and can
    // produce false combinations such as "thatting" in the visual diff.
    .replace(/[\'’`´]/g, "")
    .replace(/[.,!?;:()[\]{}"“”«»‹›…]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const splitWords = (value = "") =>
  normalizeText(value)
    .split(" ")
    .filter(Boolean);

const splitDisplayWords = (value = "") =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .filter((word) => normalizeText(word));

const getDisplayWordParts = (word = "") => {
  const value = String(word);
  const leading = value.match(/^[^\p{L}\p{N}]*/u)?.[0] || "";
  const trailing = value.match(/[^\p{L}\p{N}]*$/u)?.[0] || "";
  const end = trailing ? value.length - trailing.length : value.length;

  return {
    leading,
    core: value.slice(leading.length, end),
    trailing,
  };
};

const characterDistance = (leftText, rightText) => {
  const rows = leftText.length + 1;
  const columns = rightText.length + 1;
  const matrix = Array.from({ length: rows }, () =>
    Array(columns).fill(0)
  );

  for (let row = 0; row < rows; row += 1) matrix[row][0] = row;
  for (let column = 0; column < columns; column += 1) {
    matrix[0][column] = column;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let column = 1; column < columns; column += 1) {
      const substitutionCost =
        leftText[row - 1] === rightText[column - 1] ? 0 : 1;

      matrix[row][column] = Math.min(
        matrix[row - 1][column] + 1,
        matrix[row][column - 1] + 1,
        matrix[row - 1][column - 1] + substitutionCost
      );
    }
  }

  return matrix[leftText.length][rightText.length];
};

const wordSimilarity = (leftWord = "", rightWord = "") => {
  const left = normalizeText(leftWord);
  const right = normalizeText(rightWord);

  if (!left && !right) return 1;
  if (!left || !right) return 0;

  const distance = characterDistance(left, right);
  const longest = Math.max(left.length, right.length, 1);
  return Math.max(0, 1 - distance / longest);
};

const alignCharacters = (answerWord = "", targetWord = "") => {
  const left = String(answerWord);
  const right = String(targetWord);
  const rows = left.length + 1;
  const columns = right.length + 1;
  const matrix = Array.from({ length: rows }, () =>
    Array(columns).fill(0)
  );

  for (let row = 0; row < rows; row += 1) matrix[row][0] = row;
  for (let column = 0; column < columns; column += 1) {
    matrix[0][column] = column;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let column = 1; column < columns; column += 1) {
      const substitutionCost =
        left[row - 1] === right[column - 1] ? 0 : 1;

      matrix[row][column] = Math.min(
        matrix[row - 1][column] + 1,
        matrix[row][column - 1] + 1,
        matrix[row - 1][column - 1] + substitutionCost
      );
    }
  }

  const operations = [];
  let row = left.length;
  let column = right.length;

  while (row > 0 || column > 0) {
    if (
      row > 0 &&
      column > 0 &&
      left[row - 1] === right[column - 1] &&
      matrix[row][column] === matrix[row - 1][column - 1]
    ) {
      operations.push({
        type: "match",
        answerChar: left[row - 1],
        targetChar: right[column - 1],
      });
      row -= 1;
      column -= 1;
      continue;
    }

    if (
      row > 0 &&
      column > 0 &&
      matrix[row][column] === matrix[row - 1][column - 1] + 1
    ) {
      operations.push({
        type: "substitute",
        answerChar: left[row - 1],
        targetChar: right[column - 1],
      });
      row -= 1;
      column -= 1;
      continue;
    }

    if (row > 0 && matrix[row][column] === matrix[row - 1][column] + 1) {
      operations.push({
        type: "delete",
        answerChar: left[row - 1],
        targetChar: "",
      });
      row -= 1;
      continue;
    }

    operations.push({
      type: "insert",
      answerChar: "",
      targetChar: right[column - 1],
    });
    column -= 1;
  }

  return operations.reverse();
};

const minimumWordSimilarity = (leftWord = "", rightWord = "") => {
  const shortest = Math.min(leftWord.length, rightWord.length);

  // Very short words are too easy to match accidentally.
  if (shortest <= 2) return 1;
  if (shortest === 3) return 2 / 3;
  return 0.55;
};

const reliableWordSimilarity = (leftWord = "", rightWord = "") => {
  const shortest = Math.min(leftWord.length, rightWord.length);
  if (shortest <= 2) return 1;
  return 0.72;
};

// Stricter than the scoring matcher because the learner-facing diff must never
// invent a spelling relationship between merely similar words. For example,
// `ateep-stake` must not be paired with `sweep-steak`, and a long token must
// never be paired with a short word such as `at`. Weak pairs are shown as one
// extra word plus one missing word instead.
const isPlausibleDisplayWordPair = (leftWord = "", rightWord = "") => {
  const left = normalizeText(leftWord);
  const right = normalizeText(rightWord);

  if (!left || !right) return false;
  if (left === right) return true;

  const shortest = Math.min(left.length, right.length);
  const longest = Math.max(left.length, right.length);
  const lengthRatio = shortest / Math.max(longest, 1);
  const similarity = wordSimilarity(left, right);

  // Short words must be exact; otherwise accidental matches are too common.
  if (shortest <= 2) return false;

  // A very large length mismatch is not a spelling correction.
  if (lengthRatio < 0.6) return false;

  // Allow a single-letter difference in a three-letter word, but require a
  // stronger resemblance for normal words.
  const threshold = shortest === 3 ? 2 / 3 : 0.72;
  return similarity >= threshold;
};

// Hungarian assignment: finds the best one-to-one set of word matches globally.
// Weak pairs are represented as zero-weight edges, so they can remain unmatched.
const maximumWeightAssignment = (weights) => {
  const rowCount = weights.length;
  const columnCount = weights[0]?.length || 0;
  const size = Math.max(rowCount, columnCount);

  if (!size) return [];

  const square = Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, column) =>
      row < rowCount && column < columnCount ? weights[row][column] : 0
    )
  );

  const maxWeight = square.reduce(
    (best, row) => Math.max(best, ...row),
    0
  );
  const costs = square.map((row) =>
    row.map((weight) => maxWeight - weight)
  );

  const u = Array(size + 1).fill(0);
  const v = Array(size + 1).fill(0);
  const p = Array(size + 1).fill(0);
  const way = Array(size + 1).fill(0);

  for (let row = 1; row <= size; row += 1) {
    p[0] = row;
    let column0 = 0;
    const minValue = Array(size + 1).fill(Infinity);
    const used = Array(size + 1).fill(false);

    do {
      used[column0] = true;
      const row0 = p[column0];
      let delta = Infinity;
      let column1 = 0;

      for (let column = 1; column <= size; column += 1) {
        if (used[column]) continue;

        const current =
          costs[row0 - 1][column - 1] - u[row0] - v[column];

        if (current < minValue[column]) {
          minValue[column] = current;
          way[column] = column0;
        }

        if (minValue[column] < delta) {
          delta = minValue[column];
          column1 = column;
        }
      }

      for (let column = 0; column <= size; column += 1) {
        if (used[column]) {
          u[p[column]] += delta;
          v[column] -= delta;
        } else {
          minValue[column] -= delta;
        }
      }

      column0 = column1;
    } while (p[column0] !== 0);

    do {
      const column1 = way[column0];
      p[column0] = p[column1];
      column0 = column1;
    } while (column0 !== 0);
  }

  const assignment = Array(rowCount).fill(-1);

  for (let column = 1; column <= size; column += 1) {
    const row = p[column] - 1;
    const assignedColumn = column - 1;

    if (row >= 0 && row < rowCount && assignedColumn < columnCount) {
      assignment[row] = assignedColumn;
    }
  }

  return assignment;
};

const buildWordMatches = (answerWords, targetWords) => {
  if (!answerWords.length || !targetWords.length) return [];

  const similarities = answerWords.map((answerWord) =>
    targetWords.map((targetWord) => wordSimilarity(answerWord, targetWord))
  );

  const weights = similarities.map((row, answerIndex) =>
    row.map((similarity, targetIndex) => {
      const threshold = minimumWordSimilarity(
        answerWords[answerIndex],
        targetWords[targetIndex]
      );

      if (similarity < threshold) return 0;

      // Tiny positional tie-breaker only; lexical similarity still dominates.
      const maxPosition = Math.max(answerWords.length, targetWords.length, 1);
      const proximity =
        1 - Math.abs(answerIndex - targetIndex) / maxPosition;

      return similarity + proximity * 0.001;
    })
  );

  const assignment = maximumWeightAssignment(weights);
  const rawMatches = [];

  assignment.forEach((targetIndex, answerIndex) => {
    if (targetIndex < 0) return;

    const answerWord = answerWords[answerIndex];
    const targetWord = targetWords[targetIndex];
    const similarity = similarities[answerIndex][targetIndex];
    const threshold = minimumWordSimilarity(answerWord, targetWord);

    if (similarity < threshold) return;

    rawMatches.push({
      answerIndex,
      targetIndex,
      answerWord,
      targetWord,
      similarity,
      exact: answerWord === targetWord,
      reliable:
        similarity >= reliableWordSimilarity(answerWord, targetWord),
      characterOperations: alignCharacters(answerWord, targetWord),
    });
  });

  const reliableMatches = rawMatches.filter((match) => match.reliable);
  const reliableCoverage = targetWords.length
    ? reliableMatches.length / targetWords.length
    : 0;
  const canDiagnoseOrder =
    reliableMatches.length >= 2 && reliableCoverage >= 0.5;

  return rawMatches.map((match) => {
    const moved =
      canDiagnoseOrder &&
      match.reliable &&
      reliableMatches.some(
        (other) =>
          other !== match &&
          (other.answerIndex - match.answerIndex) *
            (other.targetIndex - match.targetIndex) <
            0
      );

    return {
      ...match,
      moved,
      canDiagnoseOrder,
    };
  });
};


// Ordered alignment used only for the learner-facing diff.
// The global assignment above remains useful for content/order scoring, but it can
// pair a misspelled word with a similar word much later in a long sentence.
// This alignment never crosses word pairs, so the visible red/green feedback stays
// attached to the local sentence context.
const buildOrderedDisplayMatches = (answerWords, targetWords) => {
  const rows = answerWords.length + 1;
  const columns = targetWords.length + 1;
  const costs = Array.from({ length: rows }, () =>
    Array(columns).fill(Infinity)
  );
  const steps = Array.from({ length: rows }, () =>
    Array(columns).fill(null)
  );

  costs[0][0] = 0;

  for (let row = 1; row < rows; row += 1) {
    costs[row][0] = row;
    steps[row][0] = "answer-only";
  }

  for (let column = 1; column < columns; column += 1) {
    costs[0][column] = column;
    steps[0][column] = "target-only";
  }

  for (let row = 1; row < rows; row += 1) {
    for (let column = 1; column < columns; column += 1) {
      const answerWord = answerWords[row - 1];
      const targetWord = targetWords[column - 1];
      const similarity = wordSimilarity(answerWord, targetWord);
      const plausiblePair = isPlausibleDisplayWordPair(
        answerWord,
        targetWord
      );

      // A weak lexical pair should be represented as one extra + one missing word,
      // not as a misleading letter-level spelling correction.
      const pairCost = plausiblePair ? 1 - similarity : 2.05;

      const candidates = [
        {
          type: "pair",
          cost: costs[row - 1][column - 1] + pairCost,
          priority: similarity === 1 ? 0 : 1,
        },
        {
          type: "answer-only",
          cost: costs[row - 1][column] + 1,
          priority: 2,
        },
        {
          type: "target-only",
          cost: costs[row][column - 1] + 1,
          priority: 3,
        },
      ];

      candidates.sort(
        (left, right) =>
          left.cost - right.cost || left.priority - right.priority
      );

      costs[row][column] = candidates[0].cost;
      steps[row][column] = candidates[0].type;
    }
  }

  const pairs = [];
  let row = answerWords.length;
  let column = targetWords.length;

  while (row > 0 || column > 0) {
    const step = steps[row][column];

    if (step === "pair") {
      const answerIndex = row - 1;
      const targetIndex = column - 1;
      const answerWord = answerWords[answerIndex];
      const targetWord = targetWords[targetIndex];
      const similarity = wordSimilarity(answerWord, targetWord);
      const plausiblePair = isPlausibleDisplayWordPair(
        answerWord,
        targetWord
      );

      // Weak pairs are clearer as deletion + insertion.
      if (plausiblePair) {
        pairs.push({
          answerIndex,
          targetIndex,
          answerWord,
          targetWord,
          similarity,
          exact: answerWord === targetWord,
          reliable:
            similarity >= reliableWordSimilarity(answerWord, targetWord),
          moved: false,
          canDiagnoseOrder: false,
          characterOperations: alignCharacters(answerWord, targetWord),
        });
      }

      row -= 1;
      column -= 1;
      continue;
    }

    if (step === "answer-only") {
      row -= 1;
      continue;
    }

    if (step === "target-only") {
      column -= 1;
      continue;
    }

    // Defensive fallback for an incomplete matrix edge.
    if (row > 0) row -= 1;
    else if (column > 0) column -= 1;
  }

  pairs.reverse();

  return {
    answerMatches: new Map(
      pairs.map((match) => [match.answerIndex, match])
    ),
    targetMatches: new Map(
      pairs.map((match) => [match.targetIndex, match])
    ),
  };
};

const calculateWeightedOrderScore = (matches, targetWords) => {
  if (!targetWords.length || !matches.length) return 0;

  const orderedMatches = [...matches].sort(
    (left, right) => left.answerIndex - right.answerIndex
  );
  const matchWeight = (match) =>
    Math.max(match.targetWord.length, 1) * match.similarity;
  const bestEndingAt = orderedMatches.map(matchWeight);

  for (let current = 0; current < orderedMatches.length; current += 1) {
    for (let previous = 0; previous < current; previous += 1) {
      if (
        orderedMatches[previous].targetIndex <
        orderedMatches[current].targetIndex
      ) {
        bestEndingAt[current] = Math.max(
          bestEndingAt[current],
          bestEndingAt[previous] + matchWeight(orderedMatches[current])
        );
      }
    }
  }

  const correctlyOrderedWeight = Math.max(...bestEndingAt, 0);
  const totalTargetWeight = targetWords.reduce(
    (sum, word) => sum + Math.max(word.length, 1),
    0
  );

  return totalTargetWeight
    ? Math.min(100, (correctlyOrderedWeight / totalTargetWeight) * 100)
    : 0;
};

export const compareTexts = (answer, target) => {
  const answerWords = splitWords(answer);
  const targetWords = splitWords(target);
  const answerDisplayWords = splitDisplayWords(answer);
  const targetDisplayWords = splitDisplayWords(target);
  const matches = buildWordMatches(answerWords, targetWords);
  const displayMatches = buildOrderedDisplayMatches(answerWords, targetWords);

  const totalAnswerWeight = answerWords.reduce(
    (sum, word) => sum + Math.max(word.length, 1),
    0
  );
  const totalTargetWeight = targetWords.reduce(
    (sum, word) => sum + Math.max(word.length, 1),
    0
  );
  const contentDenominator = Math.max(
    totalAnswerWeight,
    totalTargetWeight,
    1
  );

  const matchedContentWeight = matches.reduce(
    (sum, match) =>
      sum + match.similarity * Math.max(match.targetWord.length, 1),
    0
  );

  const contentScore = Math.min(
    100,
    (matchedContentWeight / contentDenominator) * 100
  );
  const orderScore = calculateWeightedOrderScore(matches, targetWords);
  const score = Math.max(
    0,
    Math.min(100, Math.round(contentScore * 0.65 + orderScore * 0.35))
  );

  const answerMatches = new Map(
    matches.map((match) => [match.answerIndex, match])
  );
  const targetMatches = new Map(
    matches.map((match) => [match.targetIndex, match])
  );

  const missingWords = targetWords.filter(
    (_, index) => !targetMatches.has(index)
  );
  const extraWords = answerWords.filter(
    (_, index) => !answerMatches.has(index)
  );
  const spellingDifferences = matches.filter((match) => !match.exact);
  const reliableMatches = matches.filter((match) => match.reliable);
  const canDiagnoseOrder = matches.some(
    (match) => match.canDiagnoseOrder
  );
  const movedWords = canDiagnoseOrder
    ? matches.filter((match) => match.moved)
    : [];
  const reliableCoverage = targetWords.length
    ? reliableMatches.length / targetWords.length
    : 0;
  const substantiallyDifferent =
    targetWords.length > 1 &&
    (reliableMatches.length === 0 ||
      (reliableCoverage < 0.5 && contentScore < 55));

  const errorTypes = [];

  if (spellingDifferences.length > 0) errorTypes.push("misspelling");
  if (movedWords.length > 0) errorTypes.push("order");
  if (missingWords.length > 0) errorTypes.push("missing_word");
  if (extraWords.length > 0) errorTypes.push("extra_word");
  if (substantiallyDifferent) errorTypes.push("substantially_different");

  let diagnostic = "Close match.";

  if (normalizeText(answer) === normalizeText(target)) {
    diagnostic = "Perfect match.";
  } else if (substantiallyDifferent) {
    diagnostic = "Most of the sentence differs.";
  } else if (
    movedWords.length > 0 &&
    spellingDifferences.length === 0 &&
    missingWords.length === 0 &&
    extraWords.length === 0
  ) {
    diagnostic = "Correct words, different order.";
  } else if (movedWords.length > 0 && spellingDifferences.length > 0) {
    diagnostic = "Spelling and word order differ.";
  } else if (movedWords.length > 0) {
    diagnostic = "Some words are in a different order.";
  } else if (missingWords.length > 0 && extraWords.length > 0) {
    diagnostic = "Some words are missing; others are extra.";
  } else if (missingWords.length > 0) {
    diagnostic = `${missingWords.length} ${
      missingWords.length === 1 ? "word is" : "words are"
    } missing.`;
  } else if (extraWords.length > 0) {
    diagnostic = `${extraWords.length} extra ${
      extraWords.length === 1 ? "word" : "words"
    }.`;
  } else if (spellingDifferences.length === 1) {
    const edits = spellingDifferences[0].characterOperations.filter(
      (operation) => operation.type !== "match"
    );

    if (edits.length === 1) {
      const [edit] = edits;

      if (edit.type === "delete") {
        diagnostic = `One extra letter: “${edit.answerChar}”.`;
      } else if (edit.type === "insert") {
        diagnostic = `One letter is missing: “${edit.targetChar}”.`;
      } else {
        diagnostic = `One letter differs: “${edit.answerChar}” → “${edit.targetChar}”.`;
      }
    } else {
      diagnostic = `${edits.length} letters differ in one word.`;
    }
  } else if (spellingDifferences.length > 1) {
    diagnostic = `${formatCount(spellingDifferences.length)} spelling differences.`;
  }

  return {
    score,
    diagnostic,
    answerWords,
    targetWords,
    answerDisplayWords,
    targetDisplayWords,
    answerMatches,
    targetMatches,
    displayAnswerMatches: displayMatches.answerMatches,
    displayTargetMatches: displayMatches.targetMatches,
    missingWords,
    extraWords,
    spellingDifferences,
    reliableMatches,
    canDiagnoseOrder,
    movedWords,
    substantiallyDifferent,
    errorTypes,
  };
};

export const calculateTextSimilarity = (answer, target) =>
  compareTexts(answer, target).score;

const formatCount = (count) => {
  const words = ["Zero", "One", "Two", "Three", "Four", "Five"];
  return words[count] || String(count);
};

const scoreClasses = (score) => {
  if (score >= 90) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (score >= 65) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-rose-50 text-rose-700 border-rose-200";
};

const FEEDBACK_STYLES = {
  incorrect: {
    color: "#b91c1c",
    backgroundColor: "#fee2e2",
    borderRadius: "0.25rem",
    paddingInline: "0.125rem",
    boxShadow: "inset 0 -1px 0 rgba(185, 28, 28, 0.22)",
  },
  expected: {
    color: "#15803d",
    backgroundColor: "#dcfce7",
    borderRadius: "0.25rem",
    paddingInline: "0.125rem",
    boxShadow: "inset 0 -1px 0 rgba(21, 128, 61, 0.2)",
  },
};

// Create one readable correction span instead of scattering insertion and
// deletion markers across a word. For example:
// sweep-stake -> sweep-steak
// highlights "ake" in red and "eak" in green.
const comparableCharacters = (value = "") => {
  const normalized = String(value).normalize("NFKC");
  const entries = [];

  Array.from(normalized).forEach((char, originalIndex) => {
    // Keep apostrophes visible, but ignore them as comparison units so
    // contractions remain aligned with the normalized tokenizer.
    if (/['’`´]/u.test(char)) return;

    entries.push({
      char,
      normalized: char.toLocaleLowerCase(),
      originalIndex,
    });
  });

  return entries;
};

const buildVisualCharacterDiff = (answerWord = "", targetWord = "") => {
  const answerParts = getDisplayWordParts(answerWord);
  const targetParts = getDisplayWordParts(targetWord);
  const answerEntries = comparableCharacters(answerParts.core);
  const targetEntries = comparableCharacters(targetParts.core);

  let prefixLength = 0;
  const shortestLength = Math.min(answerEntries.length, targetEntries.length);

  while (
    prefixLength < shortestLength &&
    answerEntries[prefixLength].normalized ===
      targetEntries[prefixLength].normalized
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  while (
    suffixLength < answerEntries.length - prefixLength &&
    suffixLength < targetEntries.length - prefixLength &&
    answerEntries[answerEntries.length - 1 - suffixLength].normalized ===
      targetEntries[targetEntries.length - 1 - suffixLength].normalized
  ) {
    suffixLength += 1;
  }

  const splitCore = (parts, entries) => {
    const changedStartEntry = entries[prefixLength];
    const changedEndEntry = entries[entries.length - suffixLength - 1];

    const changedStart = changedStartEntry
      ? changedStartEntry.originalIndex
      : parts.core.length;
    const changedEnd = changedEndEntry
      ? changedEndEntry.originalIndex + changedEndEntry.char.length
      : changedStart;

    return {
      leading: parts.leading,
      before: parts.core.slice(0, changedStart),
      changed: parts.core.slice(changedStart, changedEnd),
      after: parts.core.slice(changedEnd),
      trailing: parts.trailing,
    };
  };

  return {
    answer: splitCore(answerParts, answerEntries),
    target: splitCore(targetParts, targetEntries),
  };
};

const CharacterDiff = ({ answerWord, targetWord, side }) => {
  const visual = buildVisualCharacterDiff(answerWord, targetWord);
  const parts = side === "answer" ? visual.answer : visual.target;
  const style =
    side === "answer" ? FEEDBACK_STYLES.incorrect : FEEDBACK_STYLES.expected;

  return (
    <span className="inline-flex items-baseline font-semibold tracking-[0.01em]">
      {parts.leading && <span>{parts.leading}</span>}
      {parts.before && <span>{parts.before}</span>}
      {parts.changed && <span style={style}>{parts.changed}</span>}
      {parts.after && <span>{parts.after}</span>}
      {parts.trailing && <span>{parts.trailing}</span>}
    </span>
  );
};

const ComparisonWord = ({
  word,
  counterpartWord,
  match,
  side,
  unmatched,
}) => {
  if (unmatched) {
    const isAnswer = side === "answer";

    return (
      <span
        className="inline-flex items-center rounded-md px-1.5 py-0.5 font-semibold"
        style={isAnswer ? FEEDBACK_STYLES.incorrect : FEEDBACK_STYLES.expected}
        title={isAnswer ? "Extra word" : "Missing word"}
      >
        {word}
      </span>
    );
  }

  const changed = Boolean(match && !match.exact);
  const moved = Boolean(match?.moved && !changed);

  return (
    <span
      className={[
        "inline-flex items-baseline rounded-md font-semibold text-slate-900",
        moved ? "border-b-2 border-amber-400" : "",
      ].join(" ")}
      title={moved ? "Different position" : undefined}
    >
      {changed ? (
        <CharacterDiff
          answerWord={side === "answer" ? word : counterpartWord}
          targetWord={side === "target" ? word : counterpartWord}
          side={side}
        />
      ) : (
        word
      )}
    </span>
  );
};

const SentenceComparison = ({ comparison, side }) => {
  const words =
    side === "answer"
      ? comparison.answerDisplayWords
      : comparison.targetDisplayWords;
  const matches =
    side === "answer"
      ? comparison.displayAnswerMatches || comparison.answerMatches
      : comparison.displayTargetMatches || comparison.targetMatches;

  return (
    <div className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1 text-[17px] leading-8 text-slate-900 sm:text-lg">
      {words.map((word, index) => {
        const match = matches.get(index);
        const counterpartWord = match
          ? side === "answer"
            ? comparison.targetDisplayWords[match.targetIndex]
            : comparison.answerDisplayWords[match.answerIndex]
          : "";

        return (
          <ComparisonWord
            key={`${side}-${index}-${word}`}
            word={word}
            counterpartWord={counterpartWord}
            match={match}
            side={side}
            unmatched={!match}
          />
        );
      })}
    </div>
  );
};

const HiddenWordCues = ({
  words = [],
  currentWord = 0,
  isPlaying = false,
  onWordPlay,
}) => {
  if (!words.length) return null;

  const interactive = typeof onWordPlay === "function";

  return (
    <div
      className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-2.5"
      aria-label={`${words.length} hidden ${words.length === 1 ? "word" : "words"}`}
    >
      {words.map((word, index) => {
        const length = String(word?.text || word || "").length;
        const width = Math.max(48, Math.min(116, length * 8 + 26));
        const active = isPlaying && currentWord === index;
        const completed = isPlaying && index < currentWord;

        return (
          <button
            key={word?.id ?? `${index}-${length}`}
            type="button"
            onClick={() => onWordPlay?.(index, word)}
            disabled={!interactive}
            aria-label={`Play from hidden word ${index + 1} of ${words.length}`}
            title={`Play from word ${index + 1}`}
            style={{ width: `${width}px` }}
            className={[
              "group relative h-10 overflow-hidden rounded-xl border transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
              interactive
                ? "cursor-pointer hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md active:translate-y-0"
                : "cursor-default",
              active
                ? "scale-[1.04] border-blue-500 bg-gradient-to-r from-blue-500 to-indigo-600 shadow-[0_12px_28px_-14px_rgba(37,99,235,0.95)] ring-2 ring-blue-200"
                : completed
                ? "border-blue-200 bg-gradient-to-b from-blue-100 to-indigo-100/80 shadow-sm"
                : "border-blue-200/80 bg-gradient-to-b from-blue-50/95 to-indigo-50/75 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_4px_12px_-10px_rgba(37,99,235,0.45)]",
            ].join(" ")}
          >
            <span
              className={[
                "absolute left-1/2 top-1/2 h-1 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-200",
                active
                  ? "w-3 bg-white shadow-sm animate-pulse"
                  : completed
                  ? "w-7 bg-blue-400/80"
                  : "w-7 bg-blue-300/65 group-hover:w-9 group-hover:bg-blue-400/80",
              ].join(" ")}
            />
          </button>
        );
      })}
    </div>
  );
};

const WriteEchoModePanel = ({
  targetText,
  words = [],
  currentWord = 0,
  isPlayingSegment = false,
  onPlayReference,
  onCueWordPlay,
  rate = 1,
  setRate,
  assessment,
  onStageChange,
  children,
}) => {
  const [answer, setAnswer] = useState("");
  const [stage, setStage] = useState("write");
  const [hasPlayedReference, setHasPlayedReference] = useState(false);
  const [echoWithText, setEchoWithText] = useState(false);
  const [writingAttempts, setWritingAttempts] = useState([]);
  const assessmentAtEchoStartRef = useRef(null);
  const inputRef = useRef(null);
  const checkingAnswerRef = useRef(false);

  const comparison = useMemo(
    () => compareTexts(answer, targetText),
    [answer, targetText]
  );

  const moveToStage = (nextStage, details) => {
    setStage(nextStage);
    onStageChange?.(nextStage, details);
  };

  useEffect(() => {
    setAnswer("");
    setHasPlayedReference(false);
    setEchoWithText(false);
    setWritingAttempts([]);
    checkingAnswerRef.current = false;
    assessmentAtEchoStartRef.current = null;
    setStage("write");
    onStageChange?.("write");
  }, [targetText]);

  useEffect(() => {
    if (stage !== "write" || !inputRef.current) return;

    const element = inputRef.current;
    element.style.height = "0px";
    element.style.height = `${Math.min(Math.max(element.scrollHeight, 56), 160)}px`;
  }, [answer, stage]);

  useEffect(() => {
    if (
      stage === "echo" &&
      assessment &&
      assessment !== assessmentAtEchoStartRef.current
    ) {
      moveToStage("result");
    }
  }, [assessment, stage]);

  const saveWriteError = async () => {
    if (!answer.trim() || comparison.score >= 100) return;

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/writeerror`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            incorrect_text: answer.trim(),
            correct_text: String(targetText || "").trim(),
            score: comparison.score,
            error_types: comparison.errorTypes,
            diagnostic: comparison.diagnostic,
            scoring_version: "write-echo-v1",
          }),
        }
      );

      if (!response.ok) {
        console.warn("Could not save writing error:", response.status);
      }
    } catch (error) {
      // Saving analytics must never block the exercise.
      console.warn("Could not save writing error:", error);
    }
  };

  const checkAnswer = () => {
    const submittedAnswer = answer.trim();
    if (!submittedAnswer || checkingAnswerRef.current) return;

    checkingAnswerRef.current = true;

    setWritingAttempts((current) => [
      ...current,
      {
        id: `${Date.now()}-${current.length + 1}`,
        answer: submittedAnswer,
        score: comparison.score,
        diagnostic: comparison.diagnostic,
        createdAt: Date.now(),
      },
    ]);

    void saveWriteError();
    moveToStage("review");
  };

  const playReference = () => {
    setHasPlayedReference(true);
    onPlayReference?.();

    // Playback is the first action; after it starts, move focus to the answer field.
    window.setTimeout(() => inputRef.current?.focus(), 80);
  };

  const playFromCue = (index, word) => {
    setHasPlayedReference(true);
    onCueWordPlay?.(index, word);
    window.setTimeout(() => inputRef.current?.focus(), 80);
  };

  const handleInputKeyDown = (event) => {
    const composing = event.nativeEvent?.isComposing || event.isComposing;
    if (event.key === "Enter" && !event.shiftKey && !composing) {
      event.preventDefault();
      checkAnswer();
    }
  };

  const continueToEcho = (showText) => {
    const nextShowText = Boolean(showText);
    setEchoWithText(nextShowText);
    assessmentAtEchoStartRef.current = assessment;
    moveToStage("echo", { showText: nextShowText });
  };

  const tryWritingAgain = () => {
    setAnswer("");
    setHasPlayedReference(false);
    setEchoWithText(false);
    checkingAnswerRef.current = false;
    assessmentAtEchoStartRef.current = assessment;
    moveToStage("write");
  };

  const resultCard = (
    <section className="mx-auto w-full max-w-4xl rounded-2xl border border-slate-200/90 bg-white p-5 shadow-[0_20px_60px_-42px_rgba(15,23,42,0.55)] sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
            {stage === "result" ? "Practice again" : "Listening result"}
          </p>
          <p className="mt-1.5 text-sm font-medium text-slate-700">
            {comparison.diagnostic}
          </p>
        </div>

        <span
          className={`shrink-0 rounded-full border px-3 py-1 text-sm font-bold ${scoreClasses(
            comparison.score
          )}`}
        >
          {comparison.score}% match
        </span>
      </div>

      {writingAttempts.length > 1 && (
        <div className="mt-4 rounded-xl border border-slate-200/90 bg-slate-50/70 px-3.5 py-3 sm:px-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.11em] text-slate-400">
              Writing progress
            </span>
            <span className="text-xs font-medium text-slate-500">
              {writingAttempts.length} attempts
            </span>
          </div>

          <div className="mt-2.5 flex items-center gap-1.5 overflow-x-auto pb-0.5">
            {writingAttempts.map((attempt, index) => {
              const latest = index === writingAttempts.length - 1;

              return (
                <React.Fragment key={attempt.id}>
                  {index > 0 && (
                    <span aria-hidden="true" className="shrink-0 text-slate-300">
                      →
                    </span>
                  )}
                  <span
                    className={[
                      "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
                      latest
                        ? "border-blue-200 bg-blue-50 text-blue-800 shadow-sm"
                        : "border-slate-200 bg-white text-slate-600",
                    ].join(" ")}
                    title={`Attempt ${index + 1}: ${attempt.diagnostic}`}
                  >
                    <span className={latest ? "text-blue-500" : "text-slate-400"}>
                      {index + 1}
                    </span>
                    <span>{attempt.score}%</span>
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="grid grid-cols-[6rem_minmax(0,1fr)] items-start gap-4 border-b border-slate-200 px-4 py-3.5 sm:grid-cols-[7rem_minmax(0,1fr)] sm:px-5">
          <div className="pt-1 text-[11px] font-bold uppercase tracking-[0.09em] text-slate-500">
            You wrote
          </div>
          <SentenceComparison comparison={comparison} side="answer" />
        </div>

        <div className="grid grid-cols-[6rem_minmax(0,1fr)] items-start gap-4 px-4 py-3.5 sm:grid-cols-[7rem_minmax(0,1fr)] sm:px-5">
          <div className="pt-1 text-[11px] font-bold uppercase tracking-[0.09em] text-slate-500">
            Correct
          </div>
          <SentenceComparison comparison={comparison} side="target" />
        </div>
      </div>

      {(stage === "review" || stage === "result") && (
        <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => continueToEcho(false)}
            className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_-14px_rgba(37,99,235,0.9)] transition hover:-translate-y-px hover:from-blue-600 hover:to-indigo-700 active:translate-y-0"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 3l18 18" />
              <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
              <path d="M9.9 4.2A10.8 10.8 0 0 1 12 4c5.5 0 9.5 4.7 10 8a11.7 11.7 0 0 1-2.1 4.3" />
              <path d="M6.6 6.6C4.1 8.1 2.5 10.4 2 12c.8 3.3 4.5 8 10 8a10.8 10.8 0 0 0 3.1-.5" />
            </svg>
            <span>Echo without text</span>
          </button>

          <button
            type="button"
            onClick={() => continueToEcho(true)}
            className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-800 shadow-sm transition hover:-translate-y-px hover:border-blue-300 hover:bg-blue-100 active:translate-y-0"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
              <circle cx="12" cy="12" r="2.5" />
            </svg>
            <span>Echo with text</span>
          </button>

          {stage === "result" && (
            <button
              type="button"
              onClick={tryWritingAgain}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-slate-300 hover:bg-slate-50 active:translate-y-0 sm:col-span-2"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-4 w-4 shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
              </svg>
              <span>Try writing again</span>
            </button>
          )}
        </div>
      )}
    </section>
  );

  if (stage === "write") {
    const cueWords = words.length
      ? words
      : splitDisplayWords(targetText).map((text, index) => ({ id: index, text }));
    const wordCount = cueWords.length;
    const playLabel = isPlayingSegment
      ? "Stop playback"
      : hasPlayedReference
      ? "Replay sentence"
      : "Play sentence";

    return (
      <section className="mx-auto w-full max-w-4xl rounded-2xl border border-slate-200/90 bg-white p-5 shadow-[0_20px_60px_-42px_rgba(15,23,42,0.55)] sm:p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold tracking-tight text-slate-950">
            Write what you hear
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Listen, then type the sentence.
          </p>
        </div>

        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={playReference}
            aria-pressed={isPlayingSegment}
            className={[
              "group inline-flex items-center justify-center gap-3 rounded-2xl border font-semibold transition-all duration-200 active:translate-y-0",
              !hasPlayedReference && !isPlayingSegment
                ? "h-12 border-blue-500 bg-gradient-to-r from-blue-500 to-indigo-600 px-7 text-base text-white shadow-[0_14px_30px_-16px_rgba(37,99,235,0.95)] hover:-translate-y-0.5 hover:from-blue-600 hover:to-indigo-700"
                : isPlayingSegment
                ? "h-11 border-indigo-500 bg-gradient-to-r from-indigo-500 to-blue-600 px-6 text-sm text-white shadow-[0_12px_26px_-16px_rgba(79,70,229,0.9)] hover:-translate-y-0.5"
                : "h-11 border-blue-200 bg-blue-50 px-6 text-sm text-blue-800 shadow-sm hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-100",
            ].join(" ")}
          >
            <span
              aria-hidden="true"
              className={[
                "relative inline-flex h-7 w-7 items-center justify-center rounded-full transition",
                !hasPlayedReference && !isPlayingSegment
                  ? "bg-white/20 text-white"
                  : isPlayingSegment
                  ? "bg-white/20 text-white"
                  : "bg-white text-blue-700 shadow-sm",
              ].join(" ")}
            >
              {isPlayingSegment && (
                <span className="absolute inset-0 rounded-full bg-white/25 animate-ping" />
              )}
              <span className="relative text-xs leading-none">
                {isPlayingSegment ? "■" : hasPlayedReference ? "↻" : "▶"}
              </span>
            </span>
            <span>{playLabel}</span>
          </button>
        </div>

        <div className="mt-3 flex items-center justify-center gap-2">
          <span className="mr-1 text-xs font-medium text-slate-500">
            Speed
          </span>

          {[0.25, 0.5, 1].map((speed) => {
            const active = Number(rate) === speed;

            return (
              <button
                key={speed}
                type="button"
                onClick={() => setRate?.(speed)}
                aria-pressed={active}
                className={[
                  "min-w-12 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition",
                  active
                    ? "border-blue-200 bg-blue-50 text-blue-700 shadow-sm"
                    : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700",
                ].join(" ")}
              >
                {speed}×
              </button>
            );
          })}
        </div>

        <div className="mt-5 rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50/55 to-indigo-50/45 px-4 py-4">
          <HiddenWordCues
            words={cueWords}
            currentWord={currentWord}
            isPlaying={isPlayingSegment}
            onWordPlay={playFromCue}
          />
          <div className="mt-2.5 text-center text-[11px] font-medium text-blue-700/65">
            {wordCount} {wordCount === 1 ? "hidden word" : "hidden words"}
          </div>
        </div>

        <label className="mt-5 block">
          <span className="sr-only">Type what you hear</span>
          <textarea
            ref={inputRef}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Type what you hear…"
            rows={1}
            className="block min-h-14 max-h-40 w-full resize-none overflow-y-auto rounded-xl border border-slate-200 bg-white px-4 py-3 text-[16px] leading-7 text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100/70"
          />
        </label>

        <div className="mt-3 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-400">
            Press Enter to check · Shift+Enter for a new line
          </p>

          <button
            type="button"
            onClick={checkAnswer}
            disabled={!answer.trim()}
            className="inline-flex min-h-10 items-center justify-center rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_-14px_rgba(37,99,235,0.9)] transition hover:-translate-y-px hover:from-blue-600 hover:to-indigo-700 active:translate-y-0 disabled:translate-y-0 disabled:cursor-not-allowed disabled:bg-none disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
          >
            Check
          </button>
        </div>
      </section>
    );
  }

  if (stage === "review" || stage === "result") return resultCard;

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-violet-200/70 bg-violet-50/60 p-4">
        <h3 className="font-semibold text-violet-950">
          {echoWithText ? "Echo with text" : "Echo without text"}
        </h3>
        <p className="mt-1 text-sm text-violet-800/80">
          {echoWithText
            ? "Keep the sentence visible while you listen and repeat."
            : "The sentence is hidden again. Listen, then repeat from memory."}
        </p>
      </section>
      {children}
    </div>
  );
};

export default WriteEchoModePanel;